import React, { useState, useRef, useEffect } from 'react';
import Icon from '../AppIcon';
import LoadingSpinner from './LoadingSpinner';
import VideoUploadModal from './VideoUploadModal';
import VideoPlaybackModal from './VideoPlaybackModal';
import { uploadVideo, createVideoRecord } from '../../services/videoService';
import videoService from '../../services/videoService';
import { useAuth } from '../../contexts/AuthContext';

const VideoUploadButton = ({ 
  level, 
  subsection = null, 
  songId = null, 
  talentId = null,
  characterAnalysisId = null,
  onUploadSuccess, 
  onUploadError,
  className = "",
  size = "sm",
  disabled = false
}) => {
  const { user } = useAuth();
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [showPlaybackModal, setShowPlaybackModal] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoadingVideo, setIsLoadingVideo] = useState(false);
  const [error, setError] = useState(null);
  const [existingVideo, setExistingVideo] = useState(null);
  const [videoUrl, setVideoUrl] = useState(null);

  // Check for existing video on component mount and when dependencies change
  useEffect(() => {
    checkForExistingVideo();
  }, [level, subsection, songId, characterAnalysisId]);

  const checkForExistingVideo = async () => {
    if (!songId && !characterAnalysisId) return;
    
    try {
      setIsLoadingVideo(true);
      setError(null);
      
      let videoData = null;
      
      if (characterAnalysisId) {
        // Check for character-specific videos
        const { data, error } = await videoService?.getCharacterAnalysisVideos(characterAnalysisId);
        if (!error && data?.length > 0) {
          // Find video matching the level and subsection
          videoData = data?.find(video => 
            video?.level === level && 
            (video?.subsection === subsection || (!video?.subsection && !subsection))
          );
        }
      } else if (songId) {
        // Check for song-specific videos
        const { data, error } = await videoService?.getSongVideos(songId);
        if (!error && data?.length > 0) {
          // Find video matching the level and subsection
          videoData = data?.find(video => 
            video?.level === level && 
            (video?.subsection === subsection || (!video?.subsection && !subsection))
          );
        }
      }
      
      setExistingVideo(videoData || null);
      
    } catch (err) {
      console.error('Error checking for existing video:', err);
      setError('Failed to check for existing video');
    } finally {
      setIsLoadingVideo(false);
    }
  };

  // Enhanced file validation function
  const validateVideoFile = (file) => {
    if (!file) {
      return { valid: false, error: 'No file selected' };
    }

    // Check if it's actually a File object
    if (!(file instanceof File)) {
      return { valid: false, error: 'Invalid file object received' };
    }

    // Enhanced MIME type validation for video files
    const allowedMimeTypes = [
      'video/mp4',
      'video/webm', 
      'video/ogg',
      'video/avi',
      'video/mov',
      'video/quicktime',
      'video/x-msvideo',
      'video/3gpp',
      'video/x-ms-wmv'
    ];

    // First check the file's MIME type
    if (!allowedMimeTypes?.includes(file?.type)) {
      // Also check file extension as fallback
      const fileExtension = file?.name?.toLowerCase()?.split('.')?.pop();
      const allowedExtensions = ['mp4', 'webm', 'ogg', 'avi', 'mov', 'wmv', '3gp'];
      
      if (!allowedExtensions?.includes(fileExtension)) {
        return { 
          valid: false, 
          error: `Unsupported file type: ${file?.type || 'unknown'}. Please select a valid video file (MP4, WebM, MOV, AVI, etc.)` 
        };
      }
    }

    // File size validation - 500MB limit
    const maxSize = 500 * 1024 * 1024;
    if (file?.size > maxSize) {
      return { 
        valid: false, 
        error: `File too large: ${(file?.size / (1024 * 1024))?.toFixed(1)}MB. Maximum size is 500MB` 
      };
    }

    // Minimum file size check to avoid empty files
    if (file?.size < 1024) { // Less than 1KB
      return { 
        valid: false, 
        error: 'File appears to be empty or corrupted. Please select a valid video file' 
      };
    }

    return { valid: true, error: null };
  };

  const handleUpload = async (fileData) => {
    if (!fileData || !user) {
      setError('Missing file or user authentication');
      return { error: 'Upload failed: Missing required data' };
    }

    setIsUploading(true);
    setError(null);

    try {
      // Extract file from the data passed from modal
      const file = fileData?.file || fileData;
      const uploadLevel = fileData?.level || level;
      const uploadSubsection = fileData?.subsection || subsection;

      // Enhanced file validation
      const validation = validateVideoFile(file);
      if (!validation?.valid) {
        throw new Error(validation.error);
      }

      // Log file details for debugging
      console.log('Uploading video file:', {
        name: file?.name,
        type: file?.type,
        size: file?.size,
        lastModified: file?.lastModified
      });

      // Generate unique file path
      const timestamp = Date.now();
      const fileExtension = file?.name?.split('.')?.pop()?.toLowerCase() || 'mp4';
      const fileName = `${uploadLevel}${uploadSubsection ? `_${uploadSubsection}` : ''}_${timestamp}.${fileExtension}`;
      const videoPath = `${songId || 'temp'}/${talentId || user?.id}/${fileName}`;

      // Upload video with enhanced metadata
      const uploadResult = await uploadVideo(file, videoPath, fileData?.thumbnailBlob, {
        duration: fileData?.duration || 0,
        width: fileData?.width || 0,
        height: fileData?.height || 0
      });
      
      if (uploadResult?.error) {
        console.error('Video upload failed:', uploadResult?.error);
        throw new Error(uploadResult.error.message || uploadResult.error);
      }

      // Create video record in database
      let videoData = {
        song_id: songId || null,
        character_analysis_id: characterAnalysisId || null,
        talent_id: talentId || null,
        level: uploadLevel,
        subsection: uploadSubsection,
        video_path: uploadResult?.videoPath,
        thumbnail_path: uploadResult?.thumbnailPath,
        duration_seconds: fileData?.duration || 0,
        file_size_bytes: file?.size
      };

      const recordResult = await createVideoRecord(videoData, user?.id);
      
      if (recordResult?.error) {
        console.error('Failed to create video record:', recordResult?.error);
        throw new Error(`Upload successful but failed to create record: ${recordResult.error.message || recordResult.error}`);
      }

      // Success callback with enhanced data
      const successData = {
        videoPath: uploadResult?.videoPath,
        thumbnailPath: uploadResult?.thumbnailPath,
        fileName: file?.name,
        fileSize: file?.size,
        fileSizeMB: (file?.size / (1024 * 1024))?.toFixed(2),
        duration: fileData?.duration || 0,
        level: uploadLevel,
        subsection: uploadSubsection,
        metadata: uploadResult?.metadata || {},
        videoRecord: recordResult?.data
      };

      console.log('Video upload successful:', successData);
      onUploadSuccess?.(successData);

      // Update the existing video state
      setExistingVideo(recordResult?.data);

      return { success: true, data: successData };

    } catch (error) {
      console.error('Video upload error:', error);
      
      let errorMessage = error?.message || 'Failed to upload video';
      
      // Enhanced error categorization
      let errorType = 'unknown';
      if (errorMessage?.includes('size') || errorMessage?.includes('large')) {
        errorType = 'file_size';
      } else if (errorMessage?.includes('type') || errorMessage?.includes('format') || errorMessage?.includes('supported')) {
        errorType = 'file_type';
      } else if (errorMessage?.includes('Storage') || errorMessage?.includes('upload')) {
        errorType = 'storage';
      } else if (errorMessage?.includes('network') || errorMessage?.includes('connection')) {
        errorType = 'network';
      } else if (errorMessage?.includes('authentication') || errorMessage?.includes('permission')) {
        errorType = 'auth';
      }
      
      setError(errorMessage);
      onUploadError?.(errorMessage, {
        errorType,
        originalError: error,
        fileDetails: fileData ? {
          name: fileData?.file?.name || fileData?.name,
          size: fileData?.file?.size || fileData?.size,
          type: fileData?.file?.type || fileData?.type
        } : null
      });
      
      return { error: errorMessage };

    } finally {
      setIsUploading(false);
    }
  };

  const handleWatchVideo = async () => {
    if (!existingVideo) return;
    
    // Get the actual video URL before showing modal
    try {
      const url = await videoService?.getVideoUrl(existingVideo?.video_path);
      setVideoUrl(url);
      setShowPlaybackModal(true);
    } catch (err) {
      console.error('Error getting video URL:', err);
      setError('Failed to load video. Please try again.');
    }
  };

  const handleReplaceVideo = async (newVideoData) => {
    // The replace functionality is handled by the upload process
    // After successful replacement, the existing video will be updated
    setShowPlaybackModal(false);
    
    // Refresh the existing video data
    await checkForExistingVideo();
    
    // Call success callback if provided
    onUploadSuccess?.(newVideoData);
  };

  const buttonSizeClasses = {
    sm: "px-3 py-2 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg"
  };

  const iconSizes = {
    sm: 16,
    md: 18,
    lg: 20
  };

  // Determine button state and content
  const hasExistingVideo = existingVideo && existingVideo?.video_path;
  const isLoading = isLoadingVideo || isUploading;
  
  return (
    <>
      <button
        type="button"
        onClick={hasExistingVideo ? handleWatchVideo : () => setShowUploadModal(true)}
        disabled={disabled || isLoading}
        className={`
          inline-flex items-center space-x-2 
          bg-white bg-opacity-20 hover:bg-opacity-30 
          text-white border border-white border-opacity-30 
          rounded-lg transition-all duration-200
          disabled:opacity-50 disabled:cursor-not-allowed
          focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent
          ${buttonSizeClasses?.[size]}
          ${className}
        `}
      >
        {isLoading ? (
          <>
            <LoadingSpinner size="sm" className="text-white" />
            <span>{isLoadingVideo ? 'Checking...' : 'Uploading...'}</span>
          </>
        ) : hasExistingVideo ? (
          <>
            <Icon name="Play" size={iconSizes?.[size]} />
            <span>Watch Video</span>
          </>
        ) : (
          <>
            <Icon name="Video" size={iconSizes?.[size]} />
            <span>Add Video</span>
          </>
        )}
      </button>

      {/* Enhanced Error Display */}
      {error && (
        <div className="mt-2 p-2 bg-red-100 border border-red-300 rounded text-red-700 text-sm">
          <div className="flex items-start space-x-2">
            <Icon name="AlertTriangle" size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
            <span>{error}</span>
          </div>
        </div>
      )}

      {/* Video Info Display */}
      {hasExistingVideo && !error && (
        <div className="mt-2 text-xs text-blue-200">
          <div className="flex items-center space-x-2">
            <Icon name="CheckCircle" size={14} className="text-green-400" />
            <span>
              Video uploaded • {existingVideo?.duration_seconds ? 
                `${Math.floor(existingVideo?.duration_seconds / 60)}:${String(existingVideo?.duration_seconds % 60)?.padStart(2, '0')}` : 
                'Duration unknown'
              }
            </span>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      <VideoUploadModal
        isOpen={showUploadModal}
        onClose={() => {
          setShowUploadModal(false);
          setError(null);
        }}
        onUpload={handleUpload}
        level={level}
        subsection={subsection}
        isUploading={isUploading}
      />

      {/* Playback Modal */}
      {hasExistingVideo && (
        <VideoPlaybackModal
          isOpen={showPlaybackModal}
          onClose={() => setShowPlaybackModal(false)}
          videoUrl={videoUrl}
          videoData={existingVideo}
          level={level}
          subsection={subsection}
          onReplaceVideo={handleReplaceVideo}
        />
      )}
    </>
  );
};

export default VideoUploadButton;